// src/components/panels/Alerts.jsx
import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { AlertTriangle, Thermometer, Droplets, Gauge } from 'lucide-react';

const Alert = ({ icon: Icon, title, message, severity }) => {
  const severityColors = {
    high: 'bg-red-100 text-red-800 border-red-200',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    low: 'bg-blue-100 text-blue-800 border-blue-200',
    info: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <div className={`flex items-start space-x-3 p-4 rounded-lg border ${severityColors[severity]}`}>
      <Icon className="h-5 w-5 mt-0.5" />
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm mt-1">{message}</p>
      </div>
    </div>
  );
};

const Alerts = ({ data, processedData }) => {
  const alerts = useMemo(() => {
    const currentData = data[data.length - 1];
    const alerts = [];

    // Heat index alerts
    if (currentData.heatIndex > 40) {
      alerts.push({
        icon: Thermometer,
        title: 'Extreme Heat Warning',
        message: 'Heat index is at dangerous levels. Avoid outdoor activities and stay hydrated.',
        severity: 'high'
      });
    } else if (currentData.heatIndex > 32) {
      alerts.push({
        icon: Thermometer,
        title: 'Heat Advisory',
        message: 'Heat index is elevated. Take precautions during outdoor activities.',
        severity: 'medium'
      });
    }

    // Humidity alerts
    if (currentData.humidity > 80) {
      alerts.push({
        icon: Droplets,
        title: 'High Humidity Alert',
        message: 'Current humidity levels may affect comfort and increase risk of mold.',
        severity: 'medium'
      });
    } else if (currentData.humidity < 30) {
      alerts.push({
        icon: Droplets,
        title: 'Low Humidity Alert',
        message: 'Low humidity may cause discomfort and static electricity.',
        severity: 'low'
      });
    }

    // Pressure change alerts
    const pressureChange = data
      .slice(-12)
      .reduce((change, reading, index, array) => {
        if (index === 0) return 0;
        return reading.pressure - array[index - 1].pressure;
      }, 0);

    if (Math.abs(pressureChange) > 5) {
      alerts.push({
        icon: Gauge,
        title: 'Rapid Pressure Change',
        message: `Barometric pressure is ${pressureChange > 0 ? 'rising' : 'falling'} rapidly. Weather changes likely.`,
        severity: 'medium'
      });
    }

    // Dew point comfort alerts
    const dewPointTemp = currentData.dewPoint;
    if (dewPointTemp > 20) {
      alerts.push({
        icon: AlertTriangle,
        title: 'Uncomfortable Conditions',
        message: 'High dew point indicates muggy conditions.',
        severity: 'low'
      });
    }

    return alerts;
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span>Active Alerts</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {alerts.length > 0 ? (
            alerts.map((alert, index) => (
              <Alert key={index} {...alert} />
            ))
          ) : (
            <div className="text-gray-500 text-center py-4">
              No active alerts at this time
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default Alerts;